/**
 * Main ROI Calculator Component
 * Container component that orchestrates the entire calculator widget
 */

import React, { useState, useCallback, useRef, useEffect } from 'react'
import styles from './ROICalculator.module.css'
import '../styles/animations.css'
import {
  CalculationResponse,
  LoadingState,
  UnitSystem,
  IndustryType,
  MarkupType,
  CalculationRequest
} from '../types/calculator.types'

import UnitSystemToggle from './UnitSystemToggle'
import ProjectInputs from './ProjectInputs'
import DosageBagSelector from './DosageBagSelector'
import IndustrySelector from './IndustrySelector'
// import MarkupSelector from './MarkupSelector'
import ResultsDisplay from './ResultsDisplay'
import VerticalLayoutContainer from './VerticalLayoutContainer'
import ErrorSummary from './ErrorSummary'
import { TooltipProvider } from '../contexts/TooltipContext'
import { 
  convertFormErrorsToSummary, 
  createCalculationError, 
  announceValidationState,
  type ErrorItem 
} from '../utils/errorHandling'

interface ROICalculatorProps {
  apiEndpoint?: string
  citySearchApiEndpoint?: string
  className?: string
  enableAutosuggest?: boolean
  debounceMs?: number
  minCharacters?: number
  maxSuggestions?: number
}

const ROICalculator: React.FC<ROICalculatorProps> = ({
  apiEndpoint = 'https://api.example.com/calculate',
  citySearchApiEndpoint,
  className = '',
  enableAutosuggest = true,
  debounceMs = 300,
  minCharacters = 3,
  maxSuggestions = 10
}) => {
  // Form state
  const [unitSystem, setUnitSystem] = useState<UnitSystem>('imperial')
  const [area, setArea] = useState<number>(1000)
  const [thickness, setThickness] = useState<number>(6)
  const [facilityLife, setFacilityLife] = useState<number>(50) // Updated default to 50 years per requirements
  const [dosageBags, setDosageBags] = useState<number>(2) // Changed from dosagePct to dosageBags
  const [deliveryCity, setDeliveryCity] = useState<string>('Toronto')
  const [industry, setIndustry] = useState<IndustryType>('manufacturing')
  const [downtimeHours, setDowntimeHours] = useState<number>(24)
  const [markup] = useState<MarkupType>('custom')
  const [customDowntimeCost, setCustomDowntimeCost] = useState<number>(0)
  const [customMarkup] = useState<number>(0)

  // Handle unit system change and adjust dosage bags accordingly
  const handleUnitSystemChange = useCallback((newUnitSystem: UnitSystem) => {
    setUnitSystem(newUnitSystem)
    
    // Adjust dosage bags to valid range for new unit system
    if (newUnitSystem === 'imperial' && dosageBags > 3) {
      setDosageBags(3) // Max for imperial is 3 bags
    } else if (newUnitSystem === 'metric' && dosageBags < 1) {
      setDosageBags(1) // Min for metric is 1 bag
    }
    // If switching from imperial to metric, keep the same bag count if valid
    // If switching from metric to imperial, keep the same bag count if valid (1-3)
  }, [dosageBags])

  // UI state
  const [result, setResult] = useState<CalculationResponse | null>(null)
  const [loading, setLoading] = useState<LoadingState>({ isLoading: false })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [errorSummary, setErrorSummary] = useState<ErrorItem[]>([])
  const [calculationErrors, setCalculationErrors] = useState<ErrorItem[]>([])
  const [animationState, setAnimationState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')

  // Refs for scroll management
  const resultsRef = useRef<HTMLElement>(null)
  const calculateButtonRef = useRef<HTMLButtonElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Scroll management utility functions
  const scrollToResults = useCallback((behavior: ScrollBehavior = 'smooth') => {
    if (resultsRef.current) {
      // Calculate optimal scroll position - show results with some context
      const resultsRect = resultsRef.current.getBoundingClientRect()
      const viewportHeight = window.innerHeight
      const optimalOffset = Math.max(0, viewportHeight * 0.1) // 10% from top
      
      const targetY = window.scrollY + resultsRect.top - optimalOffset
      
      window.scrollTo({
        top: targetY,
        behavior
      })
    }
  }, [])

  const ensureResultsVisible = useCallback(() => {
    if (!resultsRef.current) return

    const resultsRect = resultsRef.current.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    
    // Check if results are visible in viewport
    const isResultsVisible = resultsRect.top >= 0 && resultsRect.top < viewportHeight * 0.8
    
    if (!isResultsVisible) {
      scrollToResults('smooth')
    }
  }, [scrollToResults])

  const maintainScrollPosition = useCallback(() => {
    // Store current scroll position relative to results section
    if (resultsRef.current) {
      const resultsRect = resultsRef.current.getBoundingClientRect()
      const relativePosition = resultsRect.top
      
      // Return a function to restore position
      return () => {
        if (resultsRef.current) {
          const newResultsRect = resultsRef.current.getBoundingClientRect()
          const scrollDiff = newResultsRect.top - relativePosition
          
          if (Math.abs(scrollDiff) > 5) { // Only adjust if significant change
            window.scrollBy({
              top: scrollDiff,
              behavior: 'auto' // Instant adjustment
            })
          }
        }
      }
    }
    return () => {} // No-op if no results ref
  }, [])

  // Effect to handle scroll management after results update
  useEffect(() => {
    if (result?.data && !loading.isLoading) {
      // Small delay to ensure DOM has updated
      const timeoutId = setTimeout(() => {
        ensureResultsVisible()
      }, 100)
      
      return () => clearTimeout(timeoutId)
    }
  }, [result?.data, loading.isLoading, ensureResultsVisible])

  // Validation
  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {}

    if (!area || area < 1 || area > 1000000) {
      newErrors.area = 'Area must be between 1 and 1,000,000'
    }

    if (!thickness || thickness <= 0) {
      newErrors.thickness = 'Thickness must be greater than 0'
    }

    if (!facilityLife || facilityLife < 1 || facilityLife > 100) {
      newErrors.facilityLife = 'Facility life must be between 1 and 100 years'
    }

    // Validate bag count based on unit system
    const validBags = unitSystem === 'imperial' ? [1, 2, 3] : [1, 2, 3, 4]
    if (!dosageBags || !validBags.includes(dosageBags)) {
      newErrors.dosageBags = `Dosage bags must be one of: ${validBags.join(', ')} for ${unitSystem} system`
    }

    if (!deliveryCity || deliveryCity.trim().length === 0) {
      newErrors.deliveryCity = 'Delivery city is required'
    }

    if (downtimeHours < 0 || downtimeHours > 8760) {
      newErrors.downtimeHours = 'Downtime hours must be between 0 and 8760'
    }

    if (industry === 'custom' && (!customDowntimeCost || customDowntimeCost < 0)) {
      newErrors.customDowntimeCost = 'Custom downtime cost must be a positive number'
    }

    if (markup === 'custom' && (customMarkup === null || customMarkup === undefined || customMarkup < 0)) {
      newErrors.customMarkup = 'Custom markup must be a non-negative number'
    }

    // Announce validation state changes for accessibility
    const previousErrorCount = Object.keys(errors).length
    const newErrorCount = Object.keys(newErrors).length
    
    if (previousErrorCount !== newErrorCount) {
      announceValidationState(newErrorCount, previousErrorCount)
    }

    setErrors(newErrors)
    
    // Update error summary
    const formErrorItems = convertFormErrorsToSummary(newErrors)
    setErrorSummary(formErrorItems)
    
    // If there are errors, scroll to the first one instead of showing the error box
    if (formErrorItems.length > 0) {
      // Use setTimeout to ensure the DOM has updated
      setTimeout(() => {
        const firstErrorFieldId = formErrorItems[0].fieldId
        const firstErrorField = document.getElementById(firstErrorFieldId)
        if (firstErrorField) {
          firstErrorField.focus()
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' })
          
          // Announce to screen readers
          const announcement = `${formErrorItems.length} validation ${formErrorItems.length === 1 ? 'error' : 'errors'} found. Focused on ${firstErrorFieldId.replace(/([A-Z])/g, ' $1').toLowerCase()} field`
          const liveRegion = document.createElement('div')
          liveRegion.setAttribute('aria-live', 'polite')
          liveRegion.setAttribute('aria-atomic', 'true')
          liveRegion.className = 'sr-only'
          liveRegion.textContent = announcement
          document.body.appendChild(liveRegion)
          
          // Clean up after announcement
          setTimeout(() => {
            if (document.body.contains(liveRegion)) {
              document.body.removeChild(liveRegion)
            }
          }, 1000)
        }
      }, 100)
    }
    
    return Object.keys(newErrors).length === 0
  }, [area, thickness, facilityLife, dosageBags, deliveryCity, downtimeHours, industry, customDowntimeCost, markup, customMarkup, unitSystem, errors])

  // Calculate ROI
  const calculateROI = useCallback(async () => {
    if (!validateForm()) {
      return
    }

    // Store scroll position before calculation
    const restoreScrollPosition = maintainScrollPosition()

    setLoading({ isLoading: true })
    setAnimationState('loading')
    setErrors({})
    setErrorSummary([])
    setCalculationErrors([])

    try {
      const requestData: CalculationRequest = {
        unitSystem,
        area,
        thickness,
        facilityLife,
        dosageBags, // Changed from dosagePct to dosageBags
        deliveryCity,
        industry,
        downtimeHours,
        markup,
        ...(industry === 'custom' && { customDowntimeCost }),
        ...(markup === 'custom' && { customMarkup })
      }

      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data: CalculationResponse = await response.json()

      if (!data.success) {
        throw new Error(data.error?.message || 'Calculation failed')
      }

      setResult(data)
      setCalculationErrors([]) // Clear any previous calculation errors
      setAnimationState('success')
      
      // Restore scroll position and then ensure results are visible
      restoreScrollPosition()
      
    } catch (error) {
      console.error('Calculation error:', error)
      
      // Create calculation error for display in results area
      const calcError = createCalculationError(
        error instanceof Error ? error.message : 'An unexpected error occurred',
        error instanceof Error && error.message.includes('HTTP') ? 'NETWORK_ERROR' : 'CALCULATION_ERROR'
      )
      setCalculationErrors(calcError)
      
      setResult({
        success: false,
        error: {
          code: 'CALCULATION_ERROR',
          message: error instanceof Error ? error.message : 'An unexpected error occurred'
        }
      })
      
      setAnimationState('error')
      
      // Restore scroll position for error state too
      restoreScrollPosition()
      
    } finally {
      setLoading({ isLoading: false })
      
      // Reset animation state after a delay
      setTimeout(() => {
        setAnimationState('idle')
      }, 1000)
    }
  }, [apiEndpoint, validateForm, unitSystem, area, thickness, facilityLife, dosageBags, deliveryCity, industry, downtimeHours, markup, customDowntimeCost, customMarkup, maintainScrollPosition])

  return (
    <TooltipProvider>
      <div 
        className={`${styles.calculator} ${className}`} 
        ref={containerRef}
        role="main"
        aria-label="ROI Calculator"
      >
      <VerticalLayoutContainer maxWidth={800} spacing="normal">
        {/* Error Summary - hidden, scroll to first error instead */}
        {errorSummary.length > 0 && (
          <ErrorSummary
            errors={errorSummary}
            position="top"
            hideBox={true}
            onErrorClick={(fieldId) => {
              const field = document.getElementById(fieldId)
              if (field) {
                field.focus()
                field.scrollIntoView({ behavior: 'smooth', block: 'center' })
              }
            }}
          />
        )}

        {/* Input Section */}
        <section 
          className={styles.inputSection}
          aria-labelledby="input-section-title"
        >
          <h2 
            id="input-section-title"
            className={styles.sectionTitle}
          >
            <svg className={styles.sectionIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            Project Parameters
          </h2>

          <div className={styles.formGroup}>
            <UnitSystemToggle
              value={unitSystem}
              onChange={handleUnitSystemChange}
              disabled={loading.isLoading}
            />

            <ProjectInputs
              unitSystem={unitSystem}
              area={area}
              thickness={thickness}
              facilityLife={facilityLife}
              deliveryCity={deliveryCity}
              onAreaChange={setArea}
              onThicknessChange={setThickness}
              onFacilityLifeChange={setFacilityLife}
              onDeliveryCityChange={setDeliveryCity}
              onCitySelect={(city) => {
                // When a city is selected from autosuggest, use the display name
                setDeliveryCity(city.displayName);
              }}
              errors={errors}
              disabled={loading.isLoading}
              enableAutosuggest={enableAutosuggest}
              citySearchApiEndpoint={citySearchApiEndpoint}
              debounceMs={debounceMs}
              minCharacters={minCharacters}
              maxSuggestions={maxSuggestions}
            />

            <DosageBagSelector
              value={dosageBags}
              onChange={setDosageBags}
              unitSystem={unitSystem}
              disabled={loading.isLoading}
            />

            <IndustrySelector
              industry={industry}
              downtimeHours={downtimeHours}
              customDowntimeCost={customDowntimeCost}
              deliveryCity={deliveryCity}
              onIndustryChange={setIndustry}
              onDowntimeHoursChange={setDowntimeHours}
              onCustomDowntimeCostChange={setCustomDowntimeCost}
              errors={errors}
              disabled={loading.isLoading}
            />

            {/* <MarkupSelector
              markup={markup}
              customMarkup={customMarkup}
              onMarkupChange={setMarkup}
              onCustomMarkupChange={setCustomMarkup}
              errors={errors}
              disabled={loading.isLoading}
            /> */}
          </div>
        </section>

        {/* Calculate Button */}
        <div className={styles.calculateButtonContainer}>
          <button
            ref={calculateButtonRef}
            type="button"
            onClick={calculateROI}
            disabled={loading.isLoading}
            className={`btn-primary-large ${styles.calculateButton} ${loading.isLoading ? styles.calculating : ''} ${result?.data ? styles.hasResults : ''} ${animationState === 'success' ? 'success-bounce' : ''} ${animationState === 'error' ? 'error-shake' : ''} button-transition button-hover-lift button-active-press focus-ring-animate`}
            aria-describedby={result?.error ? 'calculation-error' : 'calculation-status'}
            aria-live="polite"
            aria-atomic="true"
          >
            {loading.isLoading ? (
              <>
                <span className={`${styles.loadingSpinner} loading-pulse`} aria-hidden="true"></span>
                <span>Calculating</span>
              </>
            ) : (
              'Calculate ROI'
            )}
          </button>
          
          {/* Screen reader status announcements */}
          <div 
            id="calculation-status"
            className={styles.srOnly}
            aria-live="assertive"
            aria-atomic="true"
          >
            {loading.isLoading && 'Calculating ROI, please wait...'}
            {result?.data && !loading.isLoading && 'ROI calculation completed successfully'}
            {result?.error && !loading.isLoading && `Calculation failed: ${result.error.message}`}
          </div>
        </div>

        {/* Results Section */}
        <section 
          className={`${styles.resultsSection} ${animationState !== 'idle' ? 'transition-all' : ''}`} 
          ref={resultsRef}
          aria-labelledby="results-section-title"
          aria-live="polite"
          aria-atomic="false"
        >
          <h2 
            id="results-section-title"
            className={styles.sectionTitle}
          >
            <svg className={styles.sectionIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            ROI Analysis
          </h2>

          {/* Calculation Error Summary - appears in results area */}
          {calculationErrors.length > 0 && (
            <div className="fade-in">
              <ErrorSummary
                errors={calculationErrors}
                position="results-area"
                title="Calculation Error"
                showRecoveryGuidance={true}
              />
            </div>
          )}

          <div className={`${result?.data ? 'results-enter-active' : loading.isLoading ? 'fade-in' : ''}`}>
            {result?.data ? (
              <ResultsDisplay
                data={result.data}
                unitSystem={unitSystem}
                isLoading={loading.isLoading}
                error={result.error?.message}
                prominence="high"
                animateEntry={true}
              />
            ) : (
              <div 
                className={`${styles.emptyState} ${!loading.isLoading ? 'fade-in' : ''}`}
                role="status"
                aria-label="Ready to calculate ROI"
              >
                <svg className={styles.emptyStateIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <p className={styles.emptyStateText}>Ready to Calculate</p>
                <p className={styles.emptyStateSubtext}>
                  Enter your project parameters and click "Calculate ROI"
                </p>
              </div>
            )}
          </div>
        </section>
      </VerticalLayoutContainer>
    </div>
    </TooltipProvider>
  )
}

export default ROICalculator